import type { DeviceHealthStatus } from './DeviceHealthIndicator.types'

// ─── Thresholds (shared with deriveDeviceHealthStatus and its consumers) ──────

export const ONLINE_THRESHOLD_MS = 20 * 60 * 1000 //  20 min
export const STALE_THRESHOLD_MS = 60 * 60 * 1000 //  60 min
export const INIT_THRESHOLD_MS = 20 * 60 * 1000 //  20 min — product age below this = initializing
export const SERVICE_INIT_WINDOW = 8 * 60 * 1000 //   8 min — one reporting cycle + startup grace

/**
 * Derive a DeviceHealthIndicator status from signal/product timing, without
 * rendering anything. Shared by DeviceHealthIndicator itself and by any other
 * consumer (e.g. the Products map view) that needs the same status enum
 * outside of the visual indicator.
 */
export function deriveDeviceHealthStatus(
  lastSignalAt: string | null | undefined,
  productCreatedAt: string | null | undefined,
  serviceStartedAt?: number,
  isSimulated?: boolean,
  isSimulating?: boolean,
): DeviceHealthStatus {
  // Simulated products bypass the normal connection-based derivation
  if (isSimulated) {
    return isSimulating ? 'simulationActive' : 'simulationPaused'
  }

  if (!lastSignalAt) {
    if (serviceStartedAt && Date.now() - serviceStartedAt < SERVICE_INIT_WINDOW) {
      return 'initializing'
    }
    if (!productCreatedAt) return 'unknown'
    const productAgeMs = Date.now() - new Date(productCreatedAt).getTime()
    return productAgeMs < INIT_THRESHOLD_MS ? 'initializing' : 'unknown'
  }
  const ageMs = Date.now() - new Date(lastSignalAt).getTime()
  if (ageMs <= ONLINE_THRESHOLD_MS) return 'online'
  if (ageMs <= STALE_THRESHOLD_MS) return 'stale'
  return 'offline'
}

/**
 * Resolve the effective last-signal timestamp for DeviceHealthIndicator.
 * Prefer an explicit lastSignalAt, then take the max of live telemetry/shadow.
 * Matches the products list page derivation so detail and list stay in sync.
 */
export function resolveLastSignalAt(
  lastSignalAt: string | null | undefined,
  lastTelemetryAt: string | null | undefined,
  lastShadowAt: string | null | undefined,
): string | null {
  const telemetryAt = lastTelemetryAt ?? null
  const shadowAt = lastShadowAt ?? null
  if (lastSignalAt) {
    if (telemetryAt && telemetryAt > lastSignalAt) return telemetryAt
    if (shadowAt && shadowAt > lastSignalAt) return shadowAt
    return lastSignalAt
  }
  if (telemetryAt && shadowAt) return telemetryAt > shadowAt ? telemetryAt : shadowAt
  return telemetryAt ?? shadowAt ?? null
}

/** Keep the newer of two ISO timestamps (null-safe). */
export function maxIsoTimestamp(
  a: string | null | undefined,
  b: string | null | undefined,
): string | null {
  if (!a) return b ?? null
  if (!b) return a
  return a > b ? a : b
}
